import { filter, size } from 'lodash/fp';
import { MetaDataItem, Mad, OdooEmployee, OdooWorkPlace, Pointing, Leave } from '../types/models';
import { getLocation } from './Global';
import { Device } from '@capacitor/device';
import { NetworkInterface } from '@awesome-cordova-plugins/network-interface';
import { DateTime } from 'luxon';

export enum PointingState {
    Draft = '0_draft',
    StartPointing = '1_start_pointing',
    EndPointing = '2_end_pointing',
    WaitValidation = '3_waiting_for_validation',
    AdherentValidation = '4_adherent_validation',
    GeiqValidation = '5_geiq_validation',
}

export enum LeaveState {
    WaitValidation = '1_waiting_for_validation',
    AdherentValidation = '2_adherent_validation',
    AdherentRefusal = '3_adherent_refusal',
    GeiqValidation = '4_geiq_validation',
    GeiqRefusal = '5_geiq_refusal',
    Canceled = '6_canceled',
}

// COMMUN FUNCTIONS
export const getMetaData = async (withLocation: boolean = true): Promise<MetaDataItem> => {
    let position = null;
    let deviceInfoData = null;
    let deviceID = null;
    let wifi = null;
    let carrier = null;

    try {
        deviceID = await Device.getId();
    } catch (error) {
        console.error('An error occurred while retrieving device ID:', error);
    }

    try {
        deviceInfoData = await Device.getInfo();
    } catch (error) {
        console.error('An error occurred while retrieving device info:', error);
    }

    if (window.cordova) {
        try {
            wifi = await NetworkInterface.getWiFiIPAddress();
        } catch (error) {
            console.error('An error occurred while retrieving WiFi IP address:', error);
        }

        try {
            carrier = await NetworkInterface.getCarrierIPAddress();
        } catch (error) {
            console.error('An error occurred while retrieving carrier IP address:', error);
        }
    }

    if (withLocation) {
        try {
            position = await getLocation();
        } catch (error) {
            console.error('An error occurred while retrieving location:', error);
        }
    }

    return {
        position,
        deviceInfo: deviceInfoData,
        deviceID,
        ip: {
            wifi,
            carrier,
        },
    };
};

// POINTING FUNCTIONS
export const pointingInitialValue = (
    date: string,
    autoCompleteMads?: Mad[],
    autoCompleteWorkPlaces?: OdooWorkPlace[],
    autoCompleteEmployees?: OdooEmployee[]
): Pointing => {
    // TODO: Duplicate code with reducer Pointing and PointingDetail
    const employee_id =
        size(autoCompleteMads) === 1
            ? autoCompleteMads![0].employee_id[0]
            : size(autoCompleteEmployees) === 1
            ? autoCompleteEmployees![0].id
            : 0;

    const mad_id = size(autoCompleteMads) === 1 ? autoCompleteMads![0].id : -1;

    const madWorkplaces = filter((w) => w.mad_ids.includes(mad_id), autoCompleteWorkPlaces);
    const workplace_id =
        size(madWorkplaces) === 1 ? madWorkplaces[0].id : mad_id >= 0 && size(madWorkplaces) === 0 ? 0 : -1;

    const city = workplace_id > 0 ? autoCompleteWorkPlaces?.find((w) => w.id === workplace_id)?.city || '' : '';

    return {
        id: 0,
        date,
        employee_id,
        mad_id,
        workplace_id,
        city,
        real_time_start: null,
        real_time_end: null,
        input_time_start: null,
        input_time_end: null,
        lunch_night: 0,
        lunch: 0,
        travel: 0,
        absence_hours: 0,
        working_hours: 0,
        working_night_hours: 0,
        bad_weather_hours: 0,
        employee_note: '',
        adherent_note: '',
        other_workplace: '',
        other_mad: '',
        state: '',
        origin_id: 0,
        user_employee_validation_date: null,
        user_adherent_validation_date: null,
        user_adherent_id: [0, ''],
        user_geiq_validation_date: null,
        user_geiq_id: [0, ''],
        metadata: {
            start: null,
            end: null,
            confirm: null,
        },
        _access_right: {
            read: true,
            write: true,
            create: true,
            unlink: false,
        },
    };
};

// TODO: Maybe add clean of city when workplace is other
export const cleanDataBeforeSending = (pointing: Pointing): Pointing => {
    if (pointing.mad_id > 0) {
        pointing.other_mad = '';
    }
    if (pointing.workplace_id > 0) {
        pointing.other_workplace = '';
    }
    return pointing;
};

// Get the main pointings not including pointings created to keep history
export const getMainPointings = (pointings: Pointing[]): Pointing[] => filter({ origin_id: 0 }, pointings);

export const leaveInitialValue = (autoCompleteEmployees?: OdooEmployee[]): Leave => {
    const employee_id = size(autoCompleteEmployees) === 1 ? autoCompleteEmployees![0].id : 0;

    return {
        id: 0,
        name: '',
        employee_id,
        date_start: DateTime.now(),
        date_end: DateTime.now(),
        employee_note: '',
        adherent_note: '',
        geiq_note: '',
        type: 'paid_leave',
        state: '',
        mode: 'full_day',
        half_day: 'morning',
        start_half_day: 'morning',
        end_half_day: 'morning',
        create_date: '',
        user_employee_validation_date: null,
        user_adherent_validation_date: null,
        user_adherent_id: [0, ''],
        user_geiq_validation_date: null,
        user_geiq_id: [0, ''],
        _access_right: {
            read: true,
            write: true,
            create: true,
            unlink: false,
        },
    };
};

export const getRecordLabel = (record: [number, string]) => {
    return record[1];
};
